import React from 'react';
import { Badge } from 'antd';

/**
 * Checks if an LLM model is a thinking model
 * @param llm - The LLM object
 * @returns true if the model is a thinking model, false otherwise
 */
export const isThinkingModel = (llm: any): boolean => {
  return llm?.model_kwargs?.thinking?.type === 'enabled';
};

/**
 * Checks if an LLM model is an Anthropic model
 * @param llm - The LLM object
 * @returns true if the model is an Anthropic model, false otherwise
 */
export const isAnthropicModel = (llm: any): boolean => {
  return llm?.provider?.toLowerCase()?.includes('anthropic') ?? false;
};

/**
 * Determines if the top_p parameter should be shown for an LLM model
 * Top_p should not be shown for:
 * - Models with reasoning_effort
 * - Anthropic models
 * @param llm - The LLM object
 * @returns true if top_p should be shown, false otherwise
 */
export const shouldShowTopP = (llm: any): boolean => {
  if (!llm) return true;

  // Hide top_p for models with reasoning_effort
  if (llm?.reasoning_effort) {
    return false;
  }

  // Hide top_p for Anthropic models
  if (isAnthropicModel(llm)) {
    return false;
  }

  return true;
};

/**
 * Gets the display text for an LLM model with appropriate suffixes
 * @param llm - The LLM object
 * @returns Formatted display text with thinking, tools, and reasoning suffixes as a string
 */
export const getLlmDisplayText = (llm: any): string => {
  let displayText = llm?.model || '';

  if (isThinkingModel(llm)) {
    displayText += ' (thinking)';
  }

  if (llm?.tools) {
    displayText += ' (tools: true)';
  }

  if (llm?.reasoning_effort) {
    displayText += ` (reasoning: ${llm.reasoning_effort})`;
  }

  return displayText;
};

/**
 * Gets the display component for an LLM model with badges
 * @param llm - The LLM object
 * @returns JSX element with model name and badges
 */
export const getLlmDisplayComponent = (llm: any): React.ReactNode => {
  const modelName = llm?.model || '';
  return (
    <span className="flex flex-col gap-1">
      <span>{modelName}</span>
      <span className="flex items-center gap-2">
        {isThinkingModel(llm) && (
          <Badge
            count="thinking"
            style={{
              backgroundColor: '#1890ff',
              fontSize: '10px',
              height: '18px',
              lineHeight: '18px',
              borderRadius: '4px',
            }}
          />
        )}
        {llm?.tools && (
          <Badge
            count="tools"
            style={{
              backgroundColor: '#52c41a',
              fontSize: '10px',
              height: '18px',
              lineHeight: '18px',
              borderRadius: '4px',
            }}
          />
        )}
        {llm?.reasoning_effort && (
          <Badge
            count={`reasoning: ${llm.reasoning_effort}`}
            style={{
              backgroundColor: '#faad14',
              fontSize: '10px',
              height: '18px',
              lineHeight: '18px',
              borderRadius: '4px',
            }}
          />
        )}
      </span>
    </span>
  );
};
